// @flow
import React from 'react';
import injectSheet from 'react-jss';
import { Parallax, Background } from 'react-parallax';
import cx from 'classnames';
import Slot from 'shared/editor/Slot';
import SocialIcon from 'shared/components/SocialIcon';
import Interest from 'shared/components/Interest';
import translations from 'shared/strings';
import AddToCalendar from 'shared/components/AddToCalendar';
import { getEventDateInfo, getEventDateInfoCN } from 'shared/lib/dateUtils';
import type { $Slot } from 'shared/templates/types';
import useLazyLoad from 'shared/hooks/useLazyLoad';
import SlideUpFadeIn from 'shared/transitions/SlideUpFadeIn';
import FadeIn from 'shared/transitions/FadeIn';
import { getWebDomain } from 'shared/lib/clientSettings';
import withThemeProvider from 'shared/hoc/withThemeProvider';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

type SocialShareIcon = {
  url: string,
  icon: string
}

type TemplateSettings = {
  region: 'US' | 'CN',
  url: string,
  start_date: {
    date: string
  },
  end_date: {
    date: string
  },
  event_name: string,
  event_description: string,
  event_location: string,
  event_address: string,
  background_image_url: string,
  background_position: string,
  background_scale: string,
  social_share: Array<SocialShareIcon>
}

type Props = {
  classes: Object,
  slots: $Slot[],
  templateSettings: TemplateSettings,
  widgetPresets: Array<Object>,
  path: string
}

function getDomain() {
  if (typeof window !== 'undefined') {
    return window.location.origin || '';
  }

  return '';
}

const Event = (props: Props) => {
  const {
    classes: c,
    templateSettings,
    slots,
    widgetPresets,
    path
  } = props;

  const {
    ref: containerRef,
    isIntersectingElement: isIntersectingContainer
  } = useLazyLoad();
  const {
    ref: socialContainerRef,
    isIntersectingElement: isIntersectingSocialContainer
  } = useLazyLoad();

  const {
    url,
    region,
    start_date: {
      date: startDate
    },
    end_date: {
      date: endDate
    },
    event_name: eventName,
    event_description: eventDescription,
    event_location: eventLocation,
    event_address: eventAddress,
    social_share: socialShareIcons = [],
  } = templateSettings;

  const timezone = (region === 'CN') ? 'Asia/Shanghai' : 'America/Los_Angeles';

  const dates = region === 'CN' ?
    getEventDateInfoCN(startDate, endDate, timezone) :
    getEventDateInfo(startDate, endDate, timezone);

  const {
    startDate: formattedStartDate,
    startTime: formattedStartTime,
    endDate: formattedEndDate,
    endTime: formattedEndTime,
    duration,
    isSameDay
  } = dates;

  const event = {
    description: eventDescription,
    duration,
    startDatetime: startDate,
    location: eventLocation,
    endDatetime: endDate,
    title: eventName,
  };

  const appDomain = getWebDomain(region);

  // Only show end date twice if the date is different
  const EventDate = (
    <div className={c.date} key={region}>
      <div className={c.dateString}>
        <i className={cx('material-icons', c.iconPadding)}>calendar_today</i>
        <div className={c.dateWrapper}>
          {
            isSameDay ? (
              <div>{ formattedStartDate }</div>
            ) : (
              <>
                <div>{ formattedStartDate } { translations.TO[region] }</div>
                <div>{ formattedEndDate }</div>
              </>
            )
          }
          <div className={c.secondaryText}>
            { `${formattedStartTime} - ${formattedEndTime}` }
          </div>
          <AddToCalendar
            event={event}
            region={region}
            className={c.addToCalendarButton}
          />
        </div>
      </div>

    </div>
  );

  const Location = (
    <div className={c.location}>
      <i className={cx('material-icons', c.iconPadding)}>location_on</i>
      <div className={c.locationWrapper}>
        <div>
          {eventLocation}
        </div>
        <div className={c.secondaryText}>
          {eventAddress}
        </div>
      </div>
    </div>
  );

  const InterestIcon = (
    <Interest
      eventName={eventName}
      eventLandingUrl={`${getDomain()}${url}`}
      region={region}
    />
  );

  const socialIcons = (color = 'white') => (
    socialShareIcons.map((link, i) => (
      <SocialIcon
        customClass={c.socialIcon}
        key={i}
        iconStyle={{ color }}
        link={link}
        generateQRCode={`${appDomain}${url}`}
      />
    ))
  );

  return (
    <div ref={containerRef}>
      <div className={c.menu}>
        <Slot id="menu-bar" slots={slots} widgetPresets={widgetPresets} path={path} />
      </div>
      <Parallax strength={400} className={c.parallaxWrapper}>
        <Background className={c.backgroundContainer}>
          <div className={c.heroContainer} />
        </Background>
      </Parallax>
      <div className={c.content}>
        <div className={c.leftCol}>
          <div className={c.headerContainer}>
            <div className={c.headerContent}>
              <SlideUpFadeIn show={isIntersectingContainer}>
                <div className={c.title}>
                  {eventName}
                </div>
              </SlideUpFadeIn>
              <hr className={c.hr} />
              <div className={c.row}>
                { InterestIcon }
                <div className={c.headerSocialIcons}>
                  { socialIcons() }
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={c.rightCol}>
          <div className={c.backLinkContainer}>
            <a className={c.backLink} href={`/${region.toLowerCase()}/event`}>
              <i className="material-icons">arrow_back</i>
              {translations.BACK_TO_INDEX[region]}
            </a>
          </div>
          <FadeIn show={isIntersectingContainer}>
            <div className={c.eventInformation}>
              { EventDate }
              { Location }
            </div>
          </FadeIn>
        </div>
        <div className={cx(c.leftCol, c.adjustedTop, c.articleCol)}>
          <div className={c.eventDescription}>
            { eventDescription }
          </div>
          <Slot id="content" slots={slots} widgetPresets={widgetPresets} path={path} />
          <SlideUpFadeIn show={isIntersectingSocialContainer}>
            <div
              className={c.socialShare}
              ref={socialContainerRef}
            >
              { socialIcons('black') }
            </div>
          </SlideUpFadeIn>
        </div>
        <div className={cx(c.rightCol, c.adjustedTop)}>
          <Slot id="right-col" slots={slots} widgetPresets={widgetPresets} path={path} />
        </div>
      </div>
      <footer id="footer" className={c.footer}>
        <Slot id="footer" slots={slots} widgetPresets={widgetPresets} path={path} />
      </footer>
    </div>
  );
};

const styles = {
  menu: {
    width: '100%',
    position: 'relative',
    zIndex: '10'
  },
  parallaxWrapper: {
    width: '101%',
    height: '75vh',
    '& .react-parallax-background-children': {
      width: '100%',
      height: '100%'
    },
    '@media(max-width: 960px)': {
      height: '40vh'
    }
  },
  backgroundContainer: {
    width: '100%',
    height: '100%'
  },
  heroContainer: {
    width: '100%',
    height: '100%',
    backgroundRepeat: 'no-repeat',
    backgroundImage: ({ templateSettings: { background_image_url: url } = {}}) => (url ? `url(${url})` : null),
    backgroundPosition: ({ templateSettings: { background_position: pos } = {}}) => (pos || 'center center'),
    backgroundSize: ({ templateSettings: { background_scale: size } = {}}) => (size || 'cover')
  },
  content: {
    width: '100%',
    maxWidth: '1596px',
    margin: 'auto',
    background: 'white',
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: '36px',
    '@media(max-width: 960px)': {
      marginBottom: '0'
    }
  },
  leftCol: {
    width: '66.7%',
    position: 'relative',
    marginTop: '-216px',
    left: '72px',
    background: 'white',
    '@media(max-width: 960px)': {
      width: '100%',
      left: '0',
      right: '0',
      marginTop: '0px'
    }
  },
  headerContainer: {
    width: '100%',
    background: 'black',
    padding: '36px',
    color: 'white',
    fontFamily: FONT_FAMILY_ROBOTO,
    '@media(max-width: 960px)': {
      padding: '18px 36px'
    }
  },
  headerContent: {
    maxWidth: '90%',
    '@media(max-width: 960px)': {
      maxWidth: '100%'
    }
  },
  title: {
    fontSize: '40px',
    fontWeight: '300',
    lineHeight: '1.5em',
    marginTop: '27px',
    '@media(max-width: 960px)': {
      fontSize: '32px',
      marginTop: '0px'
    }
  },
  hr: {
    border: 'none',
    borderBottom: '1px solid rgb(48, 49, 51)',
    marginTop: '36px',
    '@media(max-width: 960px)': {
      marginTop: '18px'
    }
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '36px',
    marginBottom: '18px',
    '@media(max-width: 960px)': {
      marginTop: '18px'
    }
  },
  headerSocialIcons: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  rightCol: {
    width: '33.3%',
    background: '#f7f7f7',
    marginTop: '-180px',
    padding: '36px',
    position: 'relative',
    zIndex: '1',
    '@media(max-width: 960px)': {
      marginTop: '0px',
      width: '100%'
    }
  },
  backLinkContainer: {
    paddingBottom: '18px',
    borderBottom: '1px solid #dddddd',
    '@media(max-width: 960px)': {
      display: 'none'
    }
  },
  backLink: {
    color: 'black',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '12px',
    textTransform: 'uppercase',
    fontFamily: FONT_FAMILY_ROBOTO,
    fontWeight: '600',
    letterSpacing: '1.2px',
    textDecoration: 'none',
    '& > .material-icons': {
      fontSize: '12px',
      marginRight: '5px'
    }
  },
  eventInformation: {
    paddingTop: '36px',
    paddingBottom: '36px',
    borderBottom: '1px solid #dddddd',
    color: 'black',
    '@media(max-width: 960px)': {
      padding: '0px',
      border: 'none'
    }
  },
  adjustedTop: {
    marginTop: '0',
    paddingTop: '26px'
  },
  articleCol: {
    left: '0px',
    paddingLeft: '72px',
    marginTop: '-72px',
    '@media(max-width: 960px)': {
      paddingLeft: '0px'
    }
  },
  eventDescription: {
    padding: '0px 36px 18px'
  },
  socialShare: {
    marginTop: '36px',
    width: '90%',
    display: 'flex',
    justifyContent: 'center'
  },
  footer: {
    width: '100%'
  },
  date: {
    lineHeight: '1.5em'
  },
  dateString: {
    display: 'flex'
  },
  iconPadding: {
    paddingTop: '2px'
  },
  dateWrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '4px',
    marginLeft: '8px'
  },
  secondaryText: {
    color: '#777777'
  },
  addToCalendarButton: {
    marginTop: '9px',
    marginBottom: '30px',
    cursor: 'pointer',
    width: 'auto'
  },
  customButtonClassName: {
    color: 'black',
    fontSize: '16px'
  },
  location: {
    display: 'flex'
  },
  locationWrapper: {
    lineHeight: '1.5em',
    marginTop: '4px',
    marginLeft: '8px'
  },
  socialIcon: {
    fontSize: '28px',
    color: 'white',
    marginLeft: '8px',
    marginRight: '8px',
    textDecoration: 'none',
    cursor: 'pointer'
  }
};

export default withThemeProvider(injectSheet(styles)(Event));
